/**
 * Perfil de uma pessoa em popup (FC.Popup.UserProfilePopup; docs/spec/friends.md, "Perfil"): a capa
 * (ou a faixa na cor da pessoa), a foto, o nome e o nível, quando foi vista, as curtidas e os textos
 * do perfil. No pé, a relação e as ações (adicionar, conversar); no ⋯, tirar das conversas e
 * bloquear. No meu perfil, cada texto se escreve ali mesmo, e dá para trocar a capa e o avatar (o
 * resto fica em Configurações → Perfil).
 */
import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { useT } from '../i18n/index.js';
import { tableRatingText } from '../room/clock/ratingsModel.js';
import { PROFILE_LIMITS, type UserProfile } from '../shared.js';
import { useApp } from '../store.js';
import { ContextMenu, type MenuAnchor, type MenuEntry } from '../ui/ContextMenu.js';
import { Button, Dialog, Spinner } from '../ui/controls.js';
import { Icon } from '../ui/icons.js';
import { StarAverage } from '../ui/StarRating.js';
import { Avatar } from './Avatar.js';
import { useAvatarPicker } from './avatarPicker.js';
import { askBlock, askFriendRequest, openDm, useCanDm } from './dm.js';
import { friendStatusText, useNow } from './FriendChatWindow.js';
import { presenceDot, seenAgo, type PresenceDot, type StatusTone } from './friendsModel.js';
import { ProfileCoverPicker, useRemoveCover } from './ProfileCoverPicker.js';
import { filledCount, preferenceChips, profileCoverSrc, profileRelation, type ProfileRelation } from './profileModel.js';
import './profile.css';

export function ProfilePopupHost() {
  const open = useApp((s) => s.profileOpen);
  if (!open) return null;
  return <ProfilePopup />;
}

function ProfilePopup() {
  const t = useT();
  const profile = useApp((s) => s.profile);
  const close = useApp((s) => s.closeProfile);
  return (
    <Dialog title={profile ? t('profile.dialog', { name: profile.user.displayName }) : t('profile.title')} onClose={close} bare className="rd-profileDialog">
      {profile ? (
        <ProfileView profile={profile} onClose={close} />
      ) : (
        <div className="rd-profile__loading">
          <Spinner />
        </div>
      )}
    </Dialog>
  );
}

function ProfileView({ profile: p, onClose }: { profile: UserProfile; onClose: () => void }) {
  const relation = useApp((s) => profileRelation(p, s.contacts));
  return (
    <div className="rd-profile" style={{ '--profile-color': p.user.color } as CSSProperties}>
      <div className="rd-profile__scroll">
        <Cover profile={p} relation={relation} onClose={onClose} />
        <Identity profile={p} />
        <Rating profile={p} />
        <TableRatings profile={p} />
        {p.isMe ? <MyTexts profile={p} /> : <Texts profile={p} />}
      </div>
      <Foot profile={p} relation={relation} onClose={onClose} />
    </div>
  );
}

/**
 * A faixa do alto: a capa que a pessoa escolheu ou, sem ela (ou se a imagem não abre), a cor da pessoa
 * com o zigue-zague. No canto, o ⋯ (no perfil dos outros) ou o Trocar capa (no meu), e o ×.
 */
function Cover({ profile: p, relation, onClose }: { profile: UserProfile; relation: ProfileRelation; onClose: () => void }) {
  const t = useT();
  const dismiss = useApp((s) => s.dismissConversation);
  // as "Outras conversas" são as de quem não é amigo
  const inOthers = useApp((s) => !!s.contacts?.others?.some((c) => c.user.id === p.user.id));
  const removeCover = useRemoveCover();
  const [menu, setMenu] = useState<MenuAnchor | null>(null);
  const [picking, setPicking] = useState(false);
  const [broken, setBroken] = useState<string | null>(null);
  const src = profileCoverSrc(p.user.id, p.cover);
  const name = p.user.displayName;
  const entries: MenuEntry[] = [];
  if (p.isMe) {
    entries.push({ label: t('profile.cover.pick'), icon: <Icon name="image" size={16} />, onSelect: () => setPicking(true) });
    if (p.cover) entries.push({ label: t('profile.cover.remove'), icon: <Icon name="trash" size={16} />, onSelect: removeCover });
  } else {
    if (inOthers) entries.push({ label: t('profile.menu.dismiss'), icon: <Icon name="close" size={16} />, onSelect: () => void dismiss(p.user.id) });
    if (relation !== 'blocked') entries.push({ label: t('profile.menu.block', { name }), icon: <Icon name="ban" size={16} />, danger: true, onSelect: () => void askBlock(p.user, t) });
  }
  /** o menu abre embaixo do botão, alinhado pela direita */
  const openMenu = (button: HTMLElement) => {
    const rect = button.getBoundingClientRect();
    setMenu({ x: rect.right, y: rect.bottom + 6, end: true });
  };
  return (
    <div className="rd-profile__cover">
      {src && broken !== src ? (
        <img className="rd-profile__coverImg" src={src} alt="" onError={() => setBroken(src)} />
      ) : (
        <svg className="rd-profile__pattern" viewBox="0 0 560 96" preserveAspectRatio="none" aria-hidden="true">
          <path d="M-40 96 L60 0 L160 96 L260 0 L360 96 L460 0 L560 96 L660 0" />
          <path d="M-40 0 L60 96 L160 0 L260 96 L360 0 L460 96 L560 0 L660 96" />
        </svg>
      )}
      <div className="rd-profile__coverActions">
        {p.isMe ? (
          // sem capa, abre direto a escolha; com capa, o menu (trocar ou tirar)
          <button
            type="button"
            className="rd-profile__coverBtn"
            aria-haspopup={p.cover ? 'menu' : 'dialog'}
            aria-expanded={p.cover ? menu !== null : undefined}
            onClick={(e) => (p.cover ? openMenu(e.currentTarget) : setPicking(true))}
          >
            <Icon name="image" size={16} />
            {t('profile.cover.change')}
          </button>
        ) : entries.length ? (
          <button type="button" className="rd-roundBtn rd-profile__round" aria-label={t('friends.more', { name })} aria-haspopup="menu" aria-expanded={menu !== null} onClick={(e) => openMenu(e.currentTarget)}>
            <Icon name="more" size={18} strokeWidth={3} />
          </button>
        ) : null}
        <button type="button" className="rd-roundBtn rd-profile__round" aria-label={t('common.close')} onClick={onClose}>
          <Icon name="close" size={18} />
        </button>
      </div>
      {menu && entries.length ? <ContextMenu anchor={menu} entries={entries} label={p.isMe ? t('profile.cover.change') : t('friends.more', { name })} onClose={() => setMenu(null)} /> : null}
      {picking ? <ProfileCoverPicker initial={p.cover} onClose={() => setPicking(false)} /> : null}
    </div>
  );
}

type PresenceLine = { dot?: PresenceDot; text: string | null; tone: StatusTone; clock: boolean };

/**
 * O ponto da foto e a linha de presença: no meu, o meu status; num amigo, a presença dele (o mesmo
 * texto do painel de amigos). De quem não é amigo não se sabe se está online (a presença só vai para
 * os amigos): sem ponto, só o "visto há".
 */
function usePresenceLine(p: UserProfile): PresenceLine {
  const t = useT();
  const now = useNow();
  const myStatus = useApp((s) => s.myStatus);
  const friend = useApp((s) => (p.isMe ? undefined : (s.contacts?.friends.find((c) => c.user.id === p.user.id) ?? s.contacts?.hidden.find((c) => c.user.id === p.user.id))));
  if (p.isMe) {
    const status = myStatus ?? 'online';
    if (status === 'online') return { dot: 'online', text: t('profile.online'), tone: 'online', clock: false };
    return status === 'busy' ? { dot: 'busy', text: t('friends.status.busy'), tone: 'busy', clock: false } : { dot: 'offline', text: t('friends.status.offlineOnly'), tone: 'offline', clock: false };
  }
  if (friend?.presence.online) {
    const status = friendStatusText(friend, false, now, t);
    return { dot: presenceDot(friend.presence), text: status.tone === 'online' ? t('profile.online') : status.text, tone: status.tone, clock: false };
  }
  const seen = seenAgo(friend?.lastSeenAt ?? p.lastSeenAt, now, 'profile.seen');
  return { dot: friend ? 'offline' : undefined, text: seen.key === 'profile.seen.unknown' ? null : t(seen.key, seen.params), tone: 'offline', clock: true };
}

/** A foto sobre a faixa, o nome com o nível, e a linha de presença e de desde quando está no RPG Desk. */
function Identity({ profile: p }: { profile: UserProfile }) {
  const t = useT();
  const line = usePresenceLine(p);
  const language = document.documentElement.lang || undefined;
  const meta: ReactNode[] = [];
  if (line.text)
    meta.push(
      <span key="presence" className={`rd-profile__presence is-${line.tone}`}>
        {line.clock ? <Icon name="clock" size={14} /> : null}
        {line.text}
      </span>,
    );
  if (p.user.createdAt) meta.push(<span key="since">{t('profile.since', { date: new Date(p.user.createdAt).toLocaleDateString(language, { month: 'short', year: 'numeric' }) })}</span>);
  return (
    <div className="rd-profile__id">
      <div className="rd-profile__who">
        <span className="rd-profile__avatar">
          <Avatar name={p.user.displayName} color={p.user.color} userId={p.user.id} avatarVersion={p.user.avatarVersion} size={80} presence={line.dot} />
        </span>
        <div className="rd-profile__nameRow">
          <h2 className="rd-profile__name">{p.user.displayName}</h2>
          <span className="rd-profile__level">{t('profile.level', { n: p.level })}</span>
        </div>
      </div>
      {meta.length ? (
        <div className="rd-profile__meta">
          {meta.flatMap((part, i) =>
            i
              ? [
                  <span key={`sep${i}`} className="rd-profile__sep" aria-hidden="true">
                    •
                  </span>,
                  part,
                ]
              : [part],
          )}
        </div>
      ) : null}
    </div>
  );
}

/** As curtidas. Nos outros, os dois botões (clicar de novo tira o voto); no meu, só as contagens. */
function Rating({ profile: p }: { profile: UserProfile }) {
  const t = useT();
  const vote = useApp((s) => s.voteUser);
  const name = p.user.displayName;
  const total = p.approvals + p.disapprovals;
  const bar = (
    <div className="rd-profile__bar" aria-hidden="true">
      {p.approvals ? <span className="is-like" style={{ flexGrow: p.approvals }} /> : null}
      {p.disapprovals ? <span className="is-dislike" style={{ flexGrow: p.disapprovals }} /> : null}
      {total ? null : <span className="is-empty" />}
    </div>
  );
  if (p.isMe)
    return (
      <section className="rd-profile__section">
        <h3 className="rd-profile__eyebrow">{t('profile.rating.mine')}</h3>
        <div className="rd-profile__card">
          <div className="rd-profile__counts">
            <span className="rd-profile__count is-like">
              <Icon name="thumbUp" size={20} />
              <strong>{p.approvals}</strong>
              <span>{t(p.approvals === 1 ? 'profile.likes.one' : 'profile.likes.many')}</span>
            </span>
            <span className="rd-profile__count is-dislike">
              <Icon name="thumbDown" size={20} />
              <strong>{p.disapprovals}</strong>
              <span>{t(p.disapprovals === 1 ? 'profile.dislikes.one' : 'profile.dislikes.many')}</span>
            </span>
          </div>
          {bar}
        </div>
      </section>
    );
  const voted = p.myVote === 1 ? 'like' : p.myVote === -1 ? 'dislike' : 'none';
  return (
    <section className="rd-profile__section">
      <h3 className="rd-profile__eyebrow">{t('profile.rating.title')}</h3>
      <div className="rd-profile__card">
        <div className="rd-profile__vote" role="group" aria-label={t('profile.vote', { name })}>
          <button
            type="button"
            className={`rd-profile__voteBtn is-like${p.myVote === 1 ? ' is-on' : ''}`}
            aria-pressed={p.myVote === 1}
            aria-label={`${t('profile.like')} · ${p.approvals}`}
            onClick={() => void vote(p.user.id, p.myVote === 1 ? 0 : 1)}
          >
            <Icon name="thumbUp" size={20} />
            {p.approvals}
          </button>
          <button
            type="button"
            className={`rd-profile__voteBtn is-dislike${p.myVote === -1 ? ' is-on' : ''}`}
            aria-pressed={p.myVote === -1}
            aria-label={`${t('profile.dislike')} · ${p.disapprovals}`}
            onClick={() => void vote(p.user.id, p.myVote === -1 ? 0 : -1)}
          >
            <Icon name="thumbDown" size={20} />
            {p.disapprovals}
          </button>
          <span className={`rd-profile__voted is-${voted}`} aria-live="polite">
            {t(`profile.voted.${voted}`)}
          </span>
        </div>
        {bar}
        <div className="rd-profile__raters">{total === 0 ? t('profile.raters.none', { name }) : t(total === 1 ? 'profile.raters.one' : 'profile.raters.many', { n: total, name })}</div>
      </div>
    </section>
  );
}

/**
 * As estrelas das sessões de jogo (docs/spec/session-clock.md, "Avaliações"): como jogador (as notas dos
 * mestres) e como mestre (as dos jogadores para as sessões que mestrou). A média só aparece com 5 ou
 * mais; com menos, a contagem. Sem nenhuma, a linha some, e sem as duas, o cartão.
 */
function TableRatings({ profile: p }: { profile: UserProfile }) {
  const t = useT();
  const ratings = p.tableRatings;
  const lines = (['asPlayer', 'asGm'] as const).flatMap((key) => {
    const text = tableRatingText(t, ratings?.[key], key === 'asPlayer' ? 'player' : 'gm');
    return text ? [{ key, text, average: ratings?.[key].average ?? null }] : [];
  });
  if (!lines.length) return null;
  return (
    <section className="rd-profile__section">
      <h3 className="rd-profile__eyebrow">{t('profile.tables.title')}</h3>
      <div className="rd-profile__card rd-profile__tables">
        {lines.map(({ key, text, average }) => (
          <div key={key} className="rd-profile__tableRow">
            <span className="rd-profile__tableLabel">{t(`profile.tables.${key}`)}</span>
            {average !== null ? <StarAverage average={average} size={16} /> : null}
            <span className="rd-profile__tableText">{text}</span>
          </div>
        ))}
      </div>
    </section>
  );
}

type FieldKey = 'bio' | 'rpg' | 'times';
/** os três textos do perfil (a capa também vai no `ProfileUpdate`, mas não é texto) */
type TextProp = keyof typeof PROFILE_LIMITS;

const FIELDS: readonly { key: FieldKey; prop: TextProp; label: string; add: string; placeholder: string; rows: number }[] = [
  { key: 'bio', prop: 'bio', label: 'profile.bio', add: 'profile.add.bio', placeholder: 'profile.placeholder.bio', rows: 4 },
  { key: 'rpg', prop: 'rpgPreferences', label: 'profile.rpg', add: 'profile.add.rpg', placeholder: 'prefs.profile.rpg.placeholder', rows: 3 },
  { key: 'times', prop: 'timePreferences', label: 'profile.times', add: 'profile.add.times', placeholder: 'prefs.profile.times.placeholder', rows: 2 },
];

/** Um texto do perfil como os outros veem: Gosta de jogar em etiquetas (se for uma lista), Costuma jogar com o calendário. */
function TextValue({ field, text }: { field: FieldKey; text: string }) {
  const chips = field === 'rpg' ? preferenceChips(text) : null;
  if (chips)
    return (
      <ul className="rd-profile__chips">
        {chips.map((chip, i) => (
          <li key={i}>{chip}</li>
        ))}
      </ul>
    );
  if (field === 'times')
    return (
      <p className="rd-profile__text rd-profile__when">
        <Icon name="calendar" size={16} />
        <span>{text}</span>
      </p>
    );
  return <p className="rd-profile__text">{text}</p>;
}

/** Os textos de outra pessoa. O que está vazio não aparece; Gosta de jogar e Costuma jogar ficam lado a lado. */
function Texts({ profile: p }: { profile: UserProfile }) {
  const t = useT();
  if (!filledCount(p)) return <p className="rd-profile__none">{t('profile.emptyAll', { name: p.user.displayName })}</p>;
  const [about, ...pair] = FIELDS.map((field) =>
    p[field.prop].trim() ? (
      <div key={field.key}>
        <h3 className="rd-profile__eyebrow">{t(field.label)}</h3>
        <TextValue field={field.key} text={p[field.prop]} />
      </div>
    ) : null,
  );
  return (
    <>
      {about ? <section className="rd-profile__section">{about}</section> : null}
      {pair.some(Boolean) ? <section className="rd-profile__section rd-profile__pair">{pair}</section> : null}
    </>
  );
}

/** "Seu perfil": cada texto se escreve ali mesmo, um por vez, e vai para o servidor ao salvar. */
function MyTexts({ profile: p }: { profile: UserProfile }) {
  const t = useT();
  const save = useApp((s) => s.saveProfile);
  const [editing, setEditing] = useState<{ key: FieldKey; draft: string } | null>(null);
  const [saving, setSaving] = useState(false);
  const area = useRef<HTMLTextAreaElement>(null);
  const filled = filledCount(p);

  // o campo que abriu ganha o foco, com o cursor no fim do texto
  const editingKey = editing?.key;
  useEffect(() => {
    const el = area.current;
    if (!editingKey || !el) return;
    el.focus();
    el.setSelectionRange(el.value.length, el.value.length);
  }, [editingKey]);

  const submit = async (prop: TextProp) => {
    if (!editing) return;
    setSaving(true);
    const ok = await save({ [prop]: editing.draft });
    setSaving(false);
    // não deu: o texto fica no campo para tentar de novo (o erro já aparece no aviso)
    if (ok) setEditing(null);
  };

  return (
    <section className="rd-profile__section">
      <div className="rd-profile__mineHead">
        <h3 className="rd-profile__eyebrow">{t('profile.mine.title')}</h3>
        <span className="rd-profile__progress">{t('profile.mine.filled', { n: filled })}</span>
        <span className="rd-profile__steps" aria-hidden="true">
          {[0, 1, 2].map((i) => (
            <span key={i} className={i < filled ? 'is-on' : undefined} />
          ))}
        </span>
      </div>
      <div className="rd-profile__fields">
        {FIELDS.map((field) => {
          const value = p[field.prop];
          if (editing?.key === field.key) {
            const id = `rd-profile-${field.key}`;
            return (
              <div key={field.key} className="rd-profile__editor">
                <label htmlFor={id}>{t(field.label)}</label>
                <textarea
                  ref={area}
                  id={id}
                  className="rd-profile__textarea"
                  rows={field.rows}
                  maxLength={PROFILE_LIMITS[field.prop]}
                  placeholder={t(field.placeholder)}
                  value={editing.draft}
                  onChange={(e) => setEditing({ key: field.key, draft: e.target.value })}
                  onKeyDown={(e) => {
                    // o Esc aqui só fecha o campo; o perfil continua aberto
                    if (e.key !== 'Escape') return;
                    e.stopPropagation();
                    setEditing(null);
                  }}
                />
                <div className="rd-profile__editorActions">
                  <Button variant="secondary" disabled={saving} onClick={() => setEditing(null)}>
                    {t('profile.cancel')}
                  </Button>
                  <Button variant="action" busy={saving} onClick={() => void submit(field.prop)}>
                    {t('profile.save')}
                  </Button>
                </div>
              </div>
            );
          }
          if (value.trim())
            return (
              <div key={field.key} className="rd-profile__field">
                <div className="rd-profile__fieldText">
                  <h4 className="rd-profile__eyebrow">{t(field.label)}</h4>
                  <TextValue field={field.key} text={value} />
                </div>
                <button type="button" className="rd-profile__edit" aria-label={t('profile.edit', { field: t(field.label) })} title={t('profile.edit', { field: t(field.label) })} onClick={() => setEditing({ key: field.key, draft: value })}>
                  <Icon name="edit" size={16} />
                </button>
              </div>
            );
          return (
            <button key={field.key} type="button" className="rd-profile__add" onClick={() => setEditing({ key: field.key, draft: '' })}>
              <Icon name="plus" size={18} />
              {t(field.add)}
            </button>
          );
        })}
      </div>
    </section>
  );
}

/** O pé: a relação com a pessoa e as ações. No meu, o recado e o Trocar avatar. */
function Foot({ profile: p, relation, onClose }: { profile: UserProfile; relation: ProfileRelation; onClose: () => void }) {
  const t = useT();
  const contactAction = useApp((s) => s.contactAction);
  // sem o painel de amigos (Configurações → Chat), não há onde a conversa abrir
  const friendsPanel = useApp((s) => s.settings.showFriendsChat);
  // com quem não é amigo, também é preciso um servidor que aceite
  const canDm = useCanDm();
  const picker = useAvatarPicker();
  const name = p.user.displayName;

  if (relation === 'me')
    return (
      <div className="rd-profile__foot">
        <span className="rd-profile__relation">{t('profile.mine.footer')}</span>
        <Button variant="secondary" icon="edit" busy={picker.busy} onClick={picker.open}>
          {t('profile.changeAvatar')}
        </Button>
        {picker.dialog}
      </div>
    );

  /** "Conversar": com o amigo (e o escondido), sempre que há o painel; com os outros, se o servidor aceita. */
  const chat = (friend: boolean) =>
    (friend ? friendsPanel : canDm) ? (
      <Button
        variant="action"
        icon="chat"
        onClick={() => {
          onClose();
          openDm(p.user.id, p.user);
        }}
      >
        {t('profile.chat')}
      </Button>
    ) : null;

  let text: string;
  let actions: ReactNode;
  switch (relation) {
    case 'friend':
      text = t('profile.relation.friend');
      actions = chat(true);
      break;
    case 'hidden':
      text = t('profile.relation.hidden');
      actions = (
        <>
          <Button variant="secondary" onClick={() => void contactAction(p.user.id, 'unhide')}>
            {t('friends.menu.unhide')}
          </Button>
          {chat(true)}
        </>
      );
      break;
    case 'blocked':
      text = t('profile.relation.blocked', { name });
      actions = (
        <Button variant="secondary" onClick={() => void contactAction(p.user.id, 'unblock')}>
          {t('friends.blocked.unblock')}
        </Button>
      );
      break;
    case 'sent':
      text = t('profile.relation.sent');
      actions = chat(false);
      break;
    case 'received':
    case 'pending':
      text = t('profile.relation.pending');
      actions = chat(false);
      break;
    default:
      text = t('profile.relation.none');
      actions = (
        <>
          <Button variant={canDm ? 'secondary' : 'action'} icon="userPlus" onClick={() => void askFriendRequest(p.user, t)}>
            {t('profile.addContact')}
          </Button>
          {chat(false)}
        </>
      );
  }
  return (
    <div className="rd-profile__foot">
      <span className="rd-profile__relation">{text}</span>
      {actions}
    </div>
  );
}
